import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../utils/apiClient";
import {
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import "dayjs/locale/en";
import "dayjs/locale/ar";
import "dayjs/locale/tr";
import "dayjs/locale/hy-am";

const AgentClientsExtra = ({ agent, info, onClose, onSuccess }) => {
  const { t, i18n } = useTranslation();

  const dayjsLocaleMap = {
    ru: "ru",
    en: "en",
    ar: "ar",
    tr: "tr",
    hy: "hy-am",
  };

  const currentLanguage = i18n.language?.split("-")[0] || "ru";
  const dayjsLocale = dayjsLocaleMap[currentLanguage] || "ru";

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = React.useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [sortModel, setSortModel] = useState([]);
  const [globalMode, setGlobalMode] = useState("view");
  const period = useMemo(
    () =>
      info?.period
        ? dayjs(info.period).locale(dayjsLocale).format("MMMM YYYY")
        : "",
    [info?.period, dayjsLocale],
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        "/clients_monthly_adjustments/leads",
        {
          params: {
            page: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            sortField: sortModel.length ? sortModel[0].field : null,
            sortOrder: sortModel.length ? sortModel[0].sort : null,
            agentId: agent.id,
          },
        },
      );

      setRows(response.data[response?.data?.key ?? "leads"]);
      setTotalRows(response.data?.total ?? 0);
    } catch (error) {
      console.error("Ошибка загрузки данных", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [paginationModel, sortModel]);

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleEditAllClick = () => {
    const updatedModel = Object.fromEntries(
      rows.map((row) => [row.id, { mode: GridRowModes.Edit }]),
    );
    setRowModesModel(updatedModel);
    setGlobalMode("edit");
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleSaveAllClick = () => {
    const updatedModel = Object.fromEntries(
      rows.map((row) => [row.id, { mode: GridRowModes.View }]),
    );
    setRowModesModel(updatedModel);
    setGlobalMode("view");
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleCancelAllClick = () => {
    const updatedModel = Object.fromEntries(
      rows.map((row) => [
        row.id,
        { mode: GridRowModes.View, ignoreModifications: true },
      ]),
    );
    setRowModesModel(updatedModel);
    setGlobalMode("view");
  };

  const processRowUpdate = async (newRow) => {
    if (+newRow.otc < 0) {
      throw new Error(t("agentClientsExtra.validation.otcMin"));
    }

    if (+newRow.markup < 0) {
      throw new Error(t("agentClientsExtra.validation.markupMin"));
    }

    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    await saveRow(newRow);
    return updatedRow;
  };

  const saveRow = async (newRow) => {
    return await apiClient.post("/clients_monthly_adjustments", {
      client_id: +newRow.id,
      client_account: newRow.account_number,
      client_tn_id: +newRow.tn_id,
      agent_id: agent.id,
      date: info?.period,
      otc: +newRow.otc,
      markup: +newRow.markup,
      comment: newRow.comment ?? "",
    });
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const columns = [
    {
      field: "account_number",
      headerName: t("agentClientsExtra.columns.accountNumber"),
      flex: 1,
    },
    {
      field: "full_name",
      headerName: t("agentClientsExtra.columns.clientFio"),
      flex: 1,
      valueGetter: (_, row) =>
        [row.surname, row.first_name, row.middle_name]
          .filter(Boolean)
          .join(" "),
    },
    {
      field: "status",
      headerName: t("agentClientsExtra.columns.status"),
      flex: 1,
    },
    {
      field: "otc",
      headerName: "OTC",
      flex: 1,
      type: "number",
      editable: true,
      valueFormatter: (value) => value ?? 0,
      renderEditCell: (params) => (
        <TextField
          size="small"
          type="number"
          value={params?.value || ""}
          onChange={(e) => {
            const newValue = Number(e.target.value);
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: newValue,
            });
          }}
          error={params.error || params?.value < 0}
          slotProps={{
            htmlInput: {
              min: 0,
              inputMode: "numeric",
              onWheel: (e) => e.currentTarget.blur(),
            },
          }}
          sx={{
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
              {
                WebkitAppearance: "none",
                margin: 0,
              },
            "& .MuiInputBase-root": {
              height: "100%",
              borderRadius: 0,
            },
          }}
        />
      ),
    },
    {
      field: "markup",
      headerName: t("agentClientsExtra.columns.markup"),
      flex: 1,
      type: "number",
      editable: true,
      valueFormatter: (value) => value ?? 0,
      renderEditCell: (params) => (
        <TextField
          size="small"
          type="number"
          value={params?.value || ""}
          onChange={(e) => {
            const newValue = Number(e.target.value);
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: newValue,
            });
          }}
          error={params.error || params?.value < 0}
          slotProps={{
            htmlInput: {
              min: 0,
              inputMode: "numeric",
              onWheel: (e) => e.currentTarget.blur(),
            },
          }}
          sx={{
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
              {
                WebkitAppearance: "none",
                margin: 0,
              },
            "& .MuiInputBase-root": {
              height: "100%",
              borderRadius: 0,
            },
          }}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon color="primary" />}
              label={t("agentClientsExtra.actions.save")}
              material={{
                sx: {
                  color: "primary.main",
                },
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label={t("agentClientsExtra.actions.cancel")}
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon color="info" />}
            label={t("agentClientsExtra.actions.edit")}
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box
      sx={{
        height: "100%",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        width: "100%",

        "& .actions": {
          color: "text.secondary",
        },
        "& .textPrimary": {
          color: "text.primary",
        },
      }}
    >
      <Alert severity="info" sx={{ mx: 2 }}>
        {t("agentClientsExtra.reportPeriod")}: <b>{period}</b>
      </Alert>

      <Stack direction="row" spacing={2} sx={{ m: 2 }}>
        {globalMode === "view" && (
          <Button
            variant="contained"
            color="info"
            onClick={handleEditAllClick}
            startIcon={<EditIcon />}
          >
            {t("agentClientsExtra.actions.editAll")}
          </Button>
        )}

        {globalMode === "edit" && (
          <>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancelAllClick}
              startIcon={<CancelIcon />}
            >
              {t("agentClientsExtra.actions.cancelAll")}
            </Button>

            <Button
              variant="contained"
              color="success"
              onClick={handleSaveAllClick}
              startIcon={<SaveIcon />}
            >
              {t("agentClientsExtra.actions.saveAll")}
            </Button>
          </>
        )}
      </Stack>

      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        editMode="row"
        pagination
        paginationMode="server"
        rowCount={totalRows}
        rowModesModel={rowModesModel}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          setPaginationModel(newModel);
        }}
        pageSizeOptions={[100]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(newModel) => setSortModel(newModel)}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          alert(error.message);
        }}
        sx={{
          "--DataGrid-rowBorderColor": "#D1D3D4",
          fontFamily: "inherit",
          background: "#ffffff",
          flex: 1,
          mx: 2,
        }}
      />
    </Box>
  );
};

export default AgentClientsExtra;
